import React, { useState, useEffect } from 'react';
import { ArrowDownOnSquareIcon, XMarkIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { shopifyService } from '../../services/shopifyService';
import { getShopifySettings } from '../../utils/supabase';
import { Link } from 'react-router-dom';

const ShopifyImport = ({ onClose, onImportComplete }) => {
  const [shopDomain, setShopDomain] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('loading'); // loading, credentials, preview, importing, complete, not_configured
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const [settings, setSettings] = useState(null);
  // Curseur pour la pagination Shopify
  const [nextCursor, setNextCursor] = useState(null); // Curseur de la page suivante
  const [hasNextPage, setHasNextPage] = useState(false); // Indique s'il y a une page suivante

  // Charger les paramètres Shopify au chargement du composant
  useEffect(() => {
    const loadShopifySettings = async () => {
      setLoading(true);
      try {
        const { data, error } = await getShopifySettings();
        
        if (error || !data) {
          setStep('not_configured');
          return;
        }
        
        // Vérifier si les paramètres requis existent
        if (!data.shop_name || !data.api_password) {
          setStep('not_configured');
          return;
        }
        
        setSettings(data);
        setShopDomain(data.shop_name);
        setAccessToken(data.api_password);
        setStep('credentials');
        
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres Shopify:', error);
        setError('Impossible de charger les paramètres. Veuillez réessayer plus tard.');
        setStep('not_configured');
      } finally {
        setLoading(false);
      }
    };
    
    loadShopifySettings();
  }, []);

  // Valider les identifiants Shopify
  const validateCredentials = () => {
    setError(null);
    
    if (!shopDomain.trim()) {
      setError('Le domaine de la boutique est requis');
      return false;
    }
    
    if (!accessToken.trim()) {
      setError('Le token d\'accès est requis');
      return false;
    }
    
    return true;
  };

  // Récupérer les produits depuis l'API Shopify (supporte la pagination)
  const fetchShopifyProducts = async (cursor = null) => {
    if (!validateCredentials()) return;
    setLoading(true);
    setError(null);
    try {
      // Vérifier d'abord les identifiants
      await shopifyService.validateCredentials(shopDomain, accessToken);
      console.log('shopDomain', shopDomain);
      console.log('accessToken', accessToken);
      // Récupérer les produits (avec pagination si cursor)
      const data = (await shopifyService.fetchProducts(shopDomain, accessToken, cursor)).data;
      console.log('data', data);
      // Formater les données pour l'affichage
      const formattedProducts = data.products.map(product => ({
        id: product.id,
        title: product.title,
        price: product.variants[0]?.price || '0.00',
        image: product.image?.src || 'https://placehold.co/60x60/png',
        inventory: product.variants.reduce((sum, variant) => sum + (variant.inventory_quantity || 0), 0),
        variants: product.variants.length,
        status: product.status,
        shopifyData: product // Garder les données brutes pour l'importation
      }));
      if (cursor) {
        // Ajout des nouveaux produits à la liste existante
        setProducts(prev => [...prev, ...formattedProducts]);
        // Conserver la sélection précédente et ajouter les nouveaux produits comme non sélectionnés
        setSelectedProducts(prev => {
          const updated = { ...prev };
          formattedProducts.forEach(product => {
            if (!(product.id in updated)) updated[product.id] = false;
          });
          return updated;
        });
      } else {
        setProducts(formattedProducts);
        // Initialiser tous les produits comme non sélectionnés
        const initialSelection = {};
        formattedProducts.forEach(product => {
          initialSelection[product.id] = false;
        });
        setSelectedProducts(initialSelection);
      }
      // Gestion du curseur de pagination
      setNextCursor(data.pageInfo?.endCursor || null);
      setHasNextPage(data.pageInfo?.hasNextPage || false);
      setLoading(false);
      setStep('preview');
    } catch (error) {
      console.error('Erreur lors de la récupération des produits:', error);
      setError(error.response?.data?.message || 'Impossible de se connecter à Shopify. Vérifiez vos identifiants.');
      setLoading(false);
    }
  };

  // Gérer la sélection/déselection des produits
  const handleProductSelection = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Sélectionner ou désélectionner tous les produits
  const handleSelectAll = (select) => {
    const newSelection = {};
    products.forEach(product => {
      newSelection[product.id] = select;
    });
    setSelectedProducts(newSelection);
  };

  // Compter les produits sélectionnés
  const countSelectedProducts = () => {
    return Object.values(selectedProducts).filter(Boolean).length;
  };

  // Importer les produits sélectionnés via l'API
  const importSelectedProducts = async () => {
    const selectedCount = countSelectedProducts();
    
    if (selectedCount === 0) {
      setError('Veuillez sélectionner au moins un produit à importer');
      return;
    }
    
    setStep('importing');
    setImportProgress(0);
    setError(null);
    
    try {
      // Récupérer les IDs des produits sélectionnés
      const productIds = Object.entries(selectedProducts)
        .filter(([_, isSelected]) => isSelected)
        .map(([id, _]) => id);
      
      // Appel API pour importer les produits
      const result = (await shopifyService.importProducts(shopDomain, accessToken, productIds)).data;
      
      // Simuler la progression pendant l'importation
      let currentProgress = 0;
      const updateInterval = setInterval(() => {
        currentProgress += Math.floor(Math.random() * 15) + 5;
        if (currentProgress >= 100) {
          currentProgress = 100;
          clearInterval(updateInterval);
          
          // Une fois à 100%, attendre un peu puis passer à l'étape terminée
          setTimeout(() => {
            setStep('complete');
            setImportResults(result);
            
            // Préparer les produits importés pour les passer au callback parent
            if (onImportComplete) {
              onImportComplete(result.importedProducts, result.importedProducts.length);
            }
          }, 500);
        }
        setImportProgress(currentProgress);
      }, 200);
      
    } catch (error) {
      console.error('Erreur lors de l\'importation des produits:', error);
      setError(error.response?.data?.message || 'Une erreur est survenue pendant l\'importation.');
      // Revenir à l'étape précédente en cas d'erreur
      setStep('preview');
    }
  };
  
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-medium text-gray-900">
            Importer des produits depuis Shopify
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'loading' && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          )}

          {step === 'not_configured' && (
            <div className="text-center py-8">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-amber-500" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Configuration Shopify nécessaire</h3>
              <p className="mt-1 text-sm text-gray-500">
                Veuillez configurer vos paramètres Shopify avant d'importer des produits.
              </p>
              <div className="mt-6">
                <Link
                  to="/settings/integrations"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={onClose}
                >
                  Configurer Shopify
                </Link>
              </div>
            </div>
          )}
          
          {step === 'credentials' && (
            <div>
              <p className="text-sm text-gray-500 mb-6">
                Connectez-vous à votre boutique Shopify pour importer vos produits.
              </p>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="shopDomain" className="block text-sm font-medium text-gray-700">
                    Domaine de la boutique *
                  </label>
                  <div className="mt-1">
                    <div className="flex rounded-md shadow-sm">
                      <input
                        type="text"
                        id="shopDomain"
                        value={shopDomain}
                        onChange={(e) => setShopDomain(e.target.value)}
                        placeholder="votre-boutique"
                        readOnly={!!settings}
                        className={`${settings ? 'bg-gray-100' : ''} focus:ring-primary-500 focus:border-primary-500 flex-1 block w-full rounded-none rounded-l-md sm:text-sm border-gray-300`}
                      />
                      <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                        .myshopify.com
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="accessToken" className="block text-sm font-medium text-gray-700">
                    Token d'accès API *
                  </label>
                  <div className="mt-1">
                    <input
                      type="password"
                      id="accessToken"
                      value={accessToken}
                      onChange={(e) => setAccessToken(e.target.value)}
                      readOnly={!!settings}
                      className={`${settings ? 'bg-gray-100' : ''} shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md`}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {settings 
                      ? 'Ces informations sont récupérées depuis vos paramètres Shopify.' 
                      : 'Vous pouvez créer un token d\'accès dans les paramètres de votre boutique Shopify, sous "Applications" > "Applications personnalisées".'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Produits disponibles</h3>
                  <p className="text-sm text-gray-500">Sélectionnez les produits à importer</p>
                </div>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={() => handleSelectAll(true)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Tout sélectionner
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAll(false)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Tout désélectionner
                  </button>
                </div>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              {products.length === 0 ? (
                <div className="text-center py-12 border border-gray-200 rounded-md">
                  <p className="text-gray-500">Aucun produit trouvé dans votre boutique Shopify.</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <span className="sr-only">Sélectionner</span>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produit
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Prix
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stock
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variations
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className={selectedProducts[product.id] ? 'bg-primary-50' : 'hover:bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedProducts[product.id] || false}
                              onChange={() => handleProductSelection(product.id)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <img className="h-10 w-10 rounded-md object-cover" src={product.image} alt={product.title} />
                              </div>
                              <div className="ml-4">
                                {/* Titre tronqué si trop long, max 200px */}
                                <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate" title={product.title}>
                                  {product.title}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{product.price} XOF</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{product.inventory}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{product.variants}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="mt-4">
                <p className="text-sm text-gray-700">
                  {countSelectedProducts()} produit(s) sélectionné(s) sur {products.length}
                </p>
                {/* Bouton de pagination Shopify */}
                {hasNextPage && (
                  <button
                    type="button"
                    onClick={() => fetchShopifyProducts(nextCursor)}
                    className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    disabled={loading}
                  >
                    {loading ? 'Chargement...' : 'Charger plus de produits'}
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="text-center py-8">
              <ArrowDownOnSquareIcon className="mx-auto h-12 w-12 text-primary-500 animate-pulse" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">Importation en cours...</h3>
              <p className="mt-1 text-sm text-gray-500">Veuillez patienter pendant que nous importons vos produits</p>
              
              <div className="mt-6 mx-auto max-w-md">
                <div className="relative pt-1">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-primary-600 bg-primary-100">
                        Progression
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-primary-600">
                        {importProgress}%
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-primary-100">
                    <div 
                      style={{ width: `${importProgress}%` }} 
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary-500 transition-all duration-300"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Importation réussie</h3>
              <p className="mt-1 text-sm text-gray-500">
                {importResults?.importedProducts?.length || countSelectedProducts()} produit(s) importé(s) avec succès depuis votre boutique Shopify
              </p>
              
              {importResults?.warnings?.length > 0 && (
                <div className="mt-6 p-4 bg-yellow-50 rounded-md">
                  <h4 className="text-sm font-medium text-yellow-800">Attention</h4>
                  <ul className="mt-2 text-sm text-yellow-700 list-disc pl-5 space-y-1">
                    {importResults.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          {step === 'credentials' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => fetchShopifyProducts(nextCursor)}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </>
          )}
          
          {step === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => setStep('credentials')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={importSelectedProducts}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Importer {countSelectedProducts()} produit(s)
              </button>
            </>
          )}
          
          {step === 'complete' && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Terminer
            </button>
          )}
          
          {step === 'not_configured' && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopifyImport; 